import { useCallback, useEffect, useRef, useState } from "react";
import { checkEmailExists } from "./authApi";

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

export type EmailExistsCheckResult = {
  exists: boolean | null;
  isVerified: boolean;
  needsEmailVerification: boolean;
};

export function useDebouncedEmailExistsCheck(email: string, enabled = true, delayMs = 500) {
  const [exists, setExists] = useState<boolean | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [checking, setChecking] = useState(false);
  const requestIdRef = useRef(0);

  const runCheck = useCallback(async (targetEmail: string): Promise<EmailExistsCheckResult> => {
    const trimmed = targetEmail.trim();
    if (!isValidEmail(trimmed)) {
      setExists(null);
      setIsVerified(false);
      setNeedsEmailVerification(false);
      return { exists: null, isVerified: false, needsEmailVerification: false };
    }

    const requestId = ++requestIdRef.current;
    setChecking(true);
    try {
      const result = await checkEmailExists(trimmed);
      if (requestIdRef.current !== requestId) {
        return { exists: null, isVerified: false, needsEmailVerification: false };
      }
      const found = result.exists;
      const verified = Boolean(result.isVerified);
      const needsVerify = found && !verified;
      setExists(found);
      setIsVerified(verified);
      setNeedsEmailVerification(needsVerify);
      return { exists: found, isVerified: verified, needsEmailVerification: needsVerify };
    } catch {
      if (requestIdRef.current !== requestId) {
        return { exists: null, isVerified: false, needsEmailVerification: false };
      }
      setExists(null);
      setIsVerified(false);
      setNeedsEmailVerification(false);
      return { exists: null, isVerified: false, needsEmailVerification: false };
    } finally {
      if (requestIdRef.current === requestId) setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setExists(null);
      setIsVerified(false);
      setNeedsEmailVerification(false);
      setChecking(false);
      return;
    }

    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setExists(null);
      setIsVerified(false);
      setNeedsEmailVerification(false);
      setChecking(false);
      return;
    }

    setExists(null);
    setIsVerified(false);
    setNeedsEmailVerification(false);
    const timer = setTimeout(() => {
      void runCheck(email);
    }, delayMs);

    return () => {
      clearTimeout(timer);
    };
  }, [email, enabled, delayMs, runCheck]);

  const checkNow = useCallback(() => runCheck(email), [email, runCheck]);

  return { exists, isVerified, needsEmailVerification, checking, checkNow };
}
